import { useState } from 'react';
import axios from 'axios';
import { Lock, Hexagon, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password,
      });
      onLogin(response.data.user, response.data.access_token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed. Access denied.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: 'var(--bg-primary)' }}
      data-testid="login-page"
    >
      {/* Animated Background Grid */}
      <div className="absolute inset-0 hex-bg opacity-50"></div>
      
      {/* Glow Effects */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>

      <div className="max-w-md w-full animate-slide-up relative z-10">
        <div className="glass-panel p-8" style={{ boxShadow: '0 0 60px rgba(6, 182, 212, 0.1)' }}>
          {/* Header */}
          <div className="text-center mb-10">
            <div className="relative inline-flex items-center justify-center w-36 h-36 mb-1">
              {/* Logo */}
              <img 
                src="/guardian-logo.png" 
                alt="Guardian | AEGIS" 
                className="w-36 h-36 object-contain drop-shadow-[0_0_15px_rgba(6,182,212,0.5)]"
              />
              {/* Status Dot */}
              <div className="absolute top-0 right-2 w-4 h-4 bg-emerald-400 rounded-full shadow-lg shadow-emerald-400/50 animate-pulse"></div>
            </div>
            
            <h1 
              className="text-2xl font-semibold text-white mb-2 tracking-[0.2em] whitespace-nowrap" 
              style={{ fontFamily: "'Cinzel', serif", textShadow: '0 0 30px rgba(6, 182, 212, 0.5)' }}
            >
              GUARDIAN | AEGIS
            </h1>
            <p className="text-cyan-400 text-xs tracking-[0.25em]" style={{ fontFamily: "'Cinzel', serif" }}>OPERATIONS COMMAND SYSTEM</p>
            
            {/* Doctrine Badges */}
            <div className="flex items-center justify-center gap-3 mt-4">
              <span className="doctrine-badge doctrine-gdt">
                <Hexagon size={12} />
                GDT
              </span>
              <span className="text-slate-600">|</span>
              <span className="doctrine-badge doctrine-pist">
                <Hexagon size={12} />
                PIST
              </span>
            </div>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-[10px] font-bold uppercase tracking-[0.2em] mb-2 text-slate-500">
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="input-tactical"
                placeholder="Enter your username"
                data-testid="username-input"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-[10px] font-bold uppercase tracking-[0.2em] mb-2 text-slate-500">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="input-tactical"
                placeholder="Enter your password"
                data-testid="password-input"
              />
            </div>

            {error && (
              <div 
                className="px-4 py-3 rounded-lg animate-fade-in flex items-center gap-3"
                style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}
                data-testid="error-message"
              >
                <Zap className="w-4 h-4 text-red-400" />
                <span className="text-red-400 text-sm">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3.5 text-base tracking-wider"
              style={{ opacity: loading ? 0.7 : 1 }}
              data-testid="login-button"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-3">
                  <div className="spinner w-5 h-5" style={{ borderWidth: '2px' }}></div>
                  AUTHENTICATING...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Lock className="w-4 h-4" />
                  SECURE LOGIN
                </span>
              )}
            </button>
          </form>

          {/* Default Credentials */}
          <div className="mt-8 pt-6 border-t border-slate-800/50">
            <p className="text-[10px] text-center mb-3 text-slate-600 tracking-wider">DEFAULT COMMANDER CREDENTIALS</p>
            <div className="bg-slate-950/50 rounded-lg p-4 border border-slate-800/50">
              <div className="flex justify-between items-center mb-2">
                <span className="text-[10px] text-slate-500 tracking-wider">USERNAME</span>
                <span className="text-sm font-mono text-cyan-400">commander.don</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[10px] text-slate-500 tracking-wider">PASSWORD</span>
                <span className="text-sm font-mono text-cyan-400">GuardianCorps@2025!</span>
              </div>
            </div>
          </div>

          {/* Security Notice */}
          <div className="mt-6 flex items-center justify-center gap-2 text-slate-600">
            <Lock className="w-3 h-3" />
            <p className="text-[10px] tracking-wider">MILITARY-GRADE ENCRYPTED CONNECTION</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
